import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import categories from "../../utils/categories";
import logo from "../../assets/digikorestudios.png";

const SongsPage = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();

  const [songs, setSongs] = useState([]);
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState(null);

  const [progress, setProgress] = useState(0); // 0–100
  const [currentTime, setCurrentTime] = useState(0); // seconds
  const [duration, setDuration] = useState(0); // seconds

  const [imagePath, setImagePath] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef(new Audio());

  // prevents timeupdate from fighting with the slider while dragging
  const isSeekingRef = useRef(false);

  // 🔹 Load songs for the selected category
  useEffect(() => {
    const currentCategory = categories.find(
      (cat) => cat.name.toLowerCase() === categoryName.toLowerCase()
    );

    if (currentCategory) setImagePath(currentCategory.image);

    if (currentCategory?.songs) {
      const formattedSongs = currentCategory.songs.map((song, index) => ({
        id: song.id,
        title: `Sample ${index + 1}`,
        url: song.url,
      }));
      setSongs(formattedSongs);
    }
  }, [categoryName]);

  // 🔹 Update progress & cleanup on unmount
  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (!audio) return;

      // While user is dragging slider, don't override the UI with timeupdate
      if (isSeekingRef.current) return;

      const dur = audio.duration || 0;
      const ct = audio.currentTime || 0;

      setDuration(dur);
      setCurrentTime(ct);

      if (dur > 0) {
        setProgress((ct / dur) * 100);
      } else {
        setProgress(0);
      }
    };

    const handleEnded = () => {
      setCurrentlyPlayingIndex(null);
      setProgress(0);
      setCurrentTime(0);
      setIsPlaying(false);
    };

    const handleLoadedMeta = () => {
      setDuration(audio.duration || 0);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", handleLoadedMeta);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("loadedmetadata", handleLoadedMeta);

      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  // 🔹 Handle play/pause
  const handlePlayPause = (index) => {
    const audio = audioRef.current;

    if (!songs || !songs[index]) {
      console.warn("Invalid song index:", index);
      return;
    }

    const selectedSong = songs[index];

    // ✅ Same song toggled
    if (currentlyPlayingIndex === index) {
      if (audio.paused) {
        audio
          .play()
          .then(() => setIsPlaying(true))
          .catch((err) => console.warn("Resume failed:", err));
      } else {
        audio.pause();
        setIsPlaying(false);
      }
      return;
    }

    // 🎵 New song selected
    audio.pause();
    audio.currentTime = 0;

    audio.src = selectedSong.url;

    audio
      .play()
      .then(() => {
        setCurrentlyPlayingIndex(index);
        setIsPlaying(true);
      })
      .catch((err) => console.warn("Playback failed:", err));
  };

  // 🔹 Handle Back button
  const handleBack = () => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setCurrentlyPlayingIndex(null);
    setIsPlaying(false);
    navigate("/jingle-collection");
  };

  const closePlayer = () => {
    const audio = audioRef.current;

    audio.pause();
    audio.currentTime = 0;

    setIsPlaying(false);
    setCurrentlyPlayingIndex(null);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
  };

  // 🔹 Format time helper
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ✅ SEEK handlers (drag slider)
  const seekToPercent = (percent) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || isNaN(audio.duration)) return;

    const nextTime = (percent / 100) * audio.duration;
    audio.currentTime = nextTime;

    // update UI instantly
    setProgress(percent);
    setCurrentTime(nextTime);
  };

  const handleSeekChange = (e) => {
    const value = Number(e.target.value); // 0–100
    seekToPercent(value);
  };

  const handleSeekStart = () => {
    isSeekingRef.current = true;
  };

  const handleSeekEnd = (e) => {
    const value = Number(e.target.value);
    seekToPercent(value);
    isSeekingRef.current = false;
  };

  // ---- DevTools redirect logic (kept as-is) ----
  useEffect(() => {
    const threshold = 160;

    const isMobile = () =>
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent) ||
      window.innerWidth < 768;

    const redirectAway = () => {
      const audio = audioRef.current;
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setCurrentlyPlayingIndex(null);
      navigate("/jingle-collection", { replace: true });
    };

    const sizeGapOpen = () =>
      window.outerWidth - window.innerWidth > threshold ||
      window.outerHeight - window.innerHeight > threshold;

    const timingOpen = () => {
      const start = performance.now();
      debugger; // eslint-disable-line no-debugger
      const end = performance.now();
      return end - start > 50;
    };

    const detectDevTools = () => {
      if (isMobile()) return;
      if (sizeGapOpen() || timingOpen()) redirectAway();
    };

    detectDevTools();
    const t = setTimeout(detectDevTools, 300);

    window.addEventListener("resize", detectDevTools);
    const interval = setInterval(detectDevTools, 800);

    return () => {
      clearTimeout(t);
      clearInterval(interval);
      window.removeEventListener("resize", detectDevTools);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50 via-white to-amber-50 relative overflow-hidden">
      <div className="flex-1">
        {/* Header */}
        <div className="container mx-auto px-4 py-8 relative z-10">
          <div className="w-full flex items-center justify-center mb-12 mt-10 max-md:mb-4">
            <div className="text-center flex flex-col justify-center items-center">
              <img src={logo} className=" w-23 h-14 mb-4" alt="Digikore Studios" />
              <h1 className="text-4xl max-md:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2 capitalize">
                {categoryName} Collection
              </h1>
              <p className="text-gray-600 text-lg">{songs.length} jingles to explore</p>
            </div>
          </div>

          {/* Songs Grid */}
          <div className="grid max-md:grid-cols-2 xl:grid-cols-6 gap-8 max-w-7xl mx-auto">
            {songs.map((song, index) => (
              <div
                key={index}
                className="group relative cursor-pointer transform transition-all duration-500 hover:scale-105 "
              >
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 group-hover:shadow-2xl transition-all duration-300">
                  <img
                    src={imagePath}
                    alt={song.title}
                    className="absolute inset-0 w-full object-cover filter group-hover:brightness-110 transition-all duration-300 -z-20 rounded-2xl h-full"
                  />
                  <div className="text-center space-y-3">
                    <h3 className="text-2xl max-md:text-xl font-bold text-white drop-shadow-2xl group-hover:text-orange-50 transition-all duration-300 tracking-tight mb-4 relative">
                      {song.title}
                    </h3>
                    <button
                      onClick={() => handlePlayPause(index)}
                      className={`w-full max-md:w-[50px] py-1 rounded-xl font-semibold transition-all duration-300 ${currentlyPlayingIndex === index
                        ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg"
                        : "bg-gray-100 text-gray-700 hover:bg-orange-200"
                        }`}
                    >
                      {currentlyPlayingIndex === index && isPlaying ? "Pause" : "Play"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Player */}
        {currentlyPlayingIndex !== null && (
          <div className="fixed bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-6 z-[90]">
            {/* Desktop layout (hidden on mobile) */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                  🎵
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-lg">
                    {songs[currentlyPlayingIndex]?.title}
                  </p>
                  <p className="text-gray-500 text-sm">{categoryName}</p>
                </div>
              </div>

              {/* Desktop seek bar */}
              <div className="flex-1 max-w-md mx-6">
                <div className="w-full">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.1"
                    value={progress}
                    onMouseDown={handleSeekStart}
                    onTouchStart={handleSeekStart}
                    onChange={handleSeekChange}
                    onMouseUp={handleSeekEnd}
                    onTouchEnd={handleSeekEnd}
                    className="w-full h-2 rounded-full cursor-pointer accent-orange-500"
                  />
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handlePlayPause(currentlyPlayingIndex)}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold shadow-lg hover:scale-105 transition-all duration-300"
                >
                  {isPlaying ? "Pause" : "Play"}
                </button>

                <button
                  onClick={closePlayer}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Mobile layout (visible on mobile) */}
            <div className="md:hidden">
              {/* Top row: Song info and controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                    🎵
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-800 truncate">
                      {songs[currentlyPlayingIndex]?.title}
                    </p>
                    <p className="text-gray-500 text-sm">{categoryName}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePlayPause(currentlyPlayingIndex)}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold shadow-lg hover:scale-105 transition-all duration-300 text-sm"
                  >
                    {isPlaying ? "Pause" : "Play"}
                  </button>
                  <button
                    onClick={closePlayer}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Mobile seek bar - full width and larger */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1 text-xs text-gray-600">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={progress}
                  onMouseDown={handleSeekStart}
                  onTouchStart={handleSeekStart}
                  onChange={handleSeekChange}
                  onMouseUp={handleSeekEnd}
                  onTouchEnd={handleSeekEnd}
                  className="w-full h-3 rounded-full cursor-pointer accent-orange-500"
                />
              </div>
            </div>
          </div>
        )}

        <div className="w-full flex justify-center items-center mt-8 mb-4">
          <button
            onClick={handleBack}
            className="group flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-200 hover:scale-105"
          >
            <span className="text-orange-600 font-semibold group-hover:text-orange-700 transition-colors">
              View All Categories
            </span>
          </button>
        </div>
        {/* Copyright Notice - Added here for prominence */}
        <div className=" mb-8 animate-slide-up delay-200">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg p-4 max-w-4xl mx-auto shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
              <div className="text-left">
                <p className="text-amber-800 font-medium mb-1">Important Notice</p>
                <p className="text-amber-700 text-sm">
                  All sample jingles are provided solely for reference purposes. We do not copy, reproduce, or imitate any existing jingles. Each jingle presented is an original work with its own unique composition and lyrics, and all jingles are protected by their respective copyrights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="w-full bg-gray-100 text-center py-4 text-sm text-gray-600">
        <p>
          For contact and support:{" "}
          <a
            href="mailto:prapti@digikore.com"
            className="text-blue-600 hover:underline"
          >
            prapti@digikore.com
          </a>
        </p>
        <p className="mt-1">© 2026 Digikore Studios Limited</p>
      </footer>
    </div>
  );
};

export default SongsPage;
